import { useEffect, useState } from "react";
import { Modal } from ".";
import Alert from "../alert/Alert";
import { X, Mail } from "lucide-react";
import { cache, request } from "../../../utils";
import { autoClearError, validateEmail } from "../../../utils/helpers";
import { User } from "../../../stores/useUserStore";

interface EmailOtpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const EmailOtpModal = ({ isOpen, onClose, onSuccess }: EmailOtpModalProps) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleClose = () => {
    setEmail("");
    setError("");
    setSuccess("");
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setEmail("");
      setError("");
      setSuccess("");
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      setError("Please enter your email address");
      autoClearError(setError);
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setError("Please enter a valid email address");
      autoClearError(setError);
      return;
    }

    // Get user from cache
    const cachedUser = cache.get("USER");
    let user: User | null = null;
    try {
      if (typeof cachedUser === "string") {
        user = JSON.parse(cachedUser);
      } else if (cachedUser && typeof cachedUser === "object") {
        user = cachedUser as User;
      }
    } catch (err) {
      console.error("Error parsing user from cache:", err);
    }

    // Check if user exists and has email
    if (!user || !user.email) {
      setError("Unable to verify email. Please try again.");
      autoClearError(setError);
      return;
    }

    const cachedEmail = user.email.toLowerCase();

    if (trimmedEmail !== cachedEmail) {
      setError("Email does not match the one in your profile");
      autoClearError(setError);
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      await request.post({
        url: "/user/otp/email",
        data: { email: trimmedEmail },
        auth: false,
      });

      setSuccess("OTP sent successfully! Check your email.");
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to send OTP. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className="max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-800 dark:bg-gray-900"
    >
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20">
              <Mail className="h-5 w-5 text-primary dark:text-white/90" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Reset PIN
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Enter your email to receive OTP
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
            type="button"
            disabled={loading}
          >
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6">
          <Alert variant="error" title="Error" message={error} />
        </div>
      )}

      {success && (
        <div className="mb-6">
          <Alert variant="success" title="Success" message={success} />
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <label
            htmlFor="email"
            className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Email Address
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-800 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-primary"
            placeholder="you@example.com"
            disabled={loading}
            autoComplete="email"
          />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Enter the email address associated with your account
          </p>
        </div>

        <div className="space-y-3">
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            {loading ? <>Sending OTP...</> : "Send OTP"}
          </button>

          <button
            type="button"
            onClick={handleClose}
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EmailOtpModal;
